import {Link, useNavigate, useParams} from "react-router-dom";
import {useProductDetails} from "../backend2/admin/product/useProduct";
import {Alert, Badge, Button, Col, Figure, Row, Stack, Table, Nav, Collapse, Modal} from "react-bootstrap";
import React, {useState} from "react";
import {config, getPriceWithVat} from "../../utils2";
import Markdown from "react-markdown";
import {ShopPageTitle} from "./_components/ShopPageTitle";
import {useBasket} from "./useShop";
import {useAlert, useAuth, useDarkMode} from "../../context";
import {OurClearAlert, ProductCardLoading, TextWithButtonLink} from "../_components";
import {AdjustQuantity} from "./_components/AdjustQuantity";
import {UserPrice} from "./_components/UserPrice";
import {centsToString} from "../backend2/admin/utilities/formatCurrency";
import {ABAccordion} from "../_components/ABAccordion";
import {MarkdownRender, RenderMarkdownGitHub} from "../../markdown/utils/utilsForMarkdown";
import {useWindowSize} from "../../hooks";
import remarkGfm from "remark-gfm";


export function ProductDetail() {
    const {pid} = useParams()
    const {value, loadingValue, errorValue, clearError} = useProductDetails(pid)

    return <div className="mx-2 mx-sm-0">
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearError}/>

        {value && !loadingValue ? <>

            <TitleField value={value}/>

            <Row>
                <Col md={6}>
                    <ProductCarousel medias={value.medias} renderCount={3}/>
                </Col>
                <Col>
                    <SummaryWithAlerts value={value}/>
                    <PriceSection value={value}/>
                </Col>
            </Row>

            <AdditionalInfo value={value}/>

            <div className="mt-4"/>
            {value.related.length > 0 && <RelatedProducts related={value.related}/>}

        </> : <div>Loading...</div>}
    </div>
}

function AdditionalInfo({value}) {
    const [selectedKey, setSelectedKey] = useState("description");

    return <>
        <Nav
            activeKey={selectedKey}
            onSelect={key => setSelectedKey(key)}
            className=" mt-4"
            variant="underline"
        >
            <Nav.Item as="li">
                <Nav.Link eventKey="description">Description</Nav.Link>
            </Nav.Item>
            {value.features && <Nav.Item as="li">
                <Nav.Link eventKey="features">Features</Nav.Link>
            </Nav.Item>}

            {value.tips.length > 0 && <Nav.Item as="li">
                <Nav.Link eventKey="tips">Tips&News</Nav.Link>
            </Nav.Item>}
        </Nav>

        {(selectedKey === "description" || selectedKey === "features") && (
            <div className="my-2">
                {/*<Markdown>{value[selectedKey]}</Markdown>*/}
                {/*{typeof value[selectedKey] === "string" ? <Markdown>{value[selectedKey]}</Markdown> : JSON.stringify(<Markdown>{value[selectedKey]}</Markdown>)}*/}
                {typeof value[selectedKey] === "string" ? <MarkdownRender markdownContent={value[selectedKey]}/> : JSON.stringify(
                    <MarkdownRender markdownContent={value[selectedKey]}/>)}
            </div>)}

        {selectedKey === "tips" && value.tips && <div className="my-2">
            {value.tips.map(item => (
                <div key={item.id} className="my-2">
                    <ABAccordion title={item.title} description={item.description} body={<RenderMarkdownGitHub github={item.url}/>}/>
                </div>
            ))}
        </div>}
    </>
}


function MarkdownWrapper({markdownContent}) {
    const navigate = useNavigate();

    return <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
            a: ({href, children}) => {
                const isRelative = !href.startsWith("http")

                if (isRelative) {

                    return (
                        <a
                            href={href}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(href);
                            }}
                            style={{cursor: 'pointer'}}
                        >
                            {children}
                        </a>
                    );
                }

                // fallback for external links
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                );
            },

            img: ({src, alt = ''}) => {
                // Extract styles from alt text
                // Usage: ![A sample image | width=200 height=100](http://example.com/image.jpg)
                const [actualAlt, sizeInfo] = alt.split('|').map(str => str.trim());
                const style = {};

                if (sizeInfo) {
                    const sizeParts = sizeInfo.split(' ');
                    sizeParts.forEach(part => {
                        const [key, value] = part.split('=');
                        if (key && value) {
                            if (['width', 'height', 'maxWidth', 'maxHeight'].includes(key)) {
                                style[key] = /^\d+$/.test(value) ? `${value}px` : value;
                            }
                        }
                    });
                }

                const relativePath = config.get_remote_api()
                const isRelative = !src.startsWith("http")
                const srcPath = isRelative ? `${relativePath}${src}` : src

                return (
                    <img
                        src={srcPath}
                        alt={actualAlt}
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            ...style,
                        }}
                    />
                );
            },

            table: ({children}) => (
                <table className="table-auto border-collapse border border-gray-300 w-full my-4">
                    {children}
                </table>
            ),
            thead: ({children}) => <thead>{children}</thead>,
            tbody: ({children}) => <tbody>{children}</tbody>,
            tr: ({children}) => <tr className="border-b border-gray-300">{children}</tr>,
            th: ({children}) => (
                <th className="border border-gray-300 px-4 py-2" style={{backgroundColor: "#0047ab", color: "white"}}>{children}</th>
            ),
            td: ({children}) => (
                <td className="border border-gray-300 px-4 py-2">{children}</td>
            ),

        }}
    >
        {markdownContent}
    </Markdown>
}

function ProductCarousel({medias, renderCount}) {
    const [count, setCount] = useState(0);
    const [selectedImage, setSelectedImage] = useState(0)
    const [show, setShow] = useState(false);
    const {darkMode} = useDarkMode()

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    const filteredMedias = medias.filter((item, index) => index < renderCount + count && index >= count)

    return <Stack className="align-items-center">
        <Figure className="m-0 mt-1" style={{cursor: "pointer"}} onClick={handleShow}>
            <Figure.Image
                src={config.get_public_media(medias[selectedImage]?.path, "medium", darkMode)}
                height="350px"
                width="350px"
                loading="lazy"
            />
        </Figure>

        <Stack direction="horizontal" className="justify-content-center align-items-center" gap={1}>
            <Button variant="light" onClick={() => setCount(prev => prev - 1)} disabled={count === 0}><i className="bi bi-chevron-left"/></Button>
            {filteredMedias.map((item, index) => (
                <Figure key={index} className="my-1">
                    <Figure.Image
                        src={config.get_public_media(item?.path, "thumb", darkMode)}
                        height="75px"
                        width="75px"
                        loading="lazy"
                        style={{cursor: "pointer"}}
                        className={selectedImage === index + count ? "border rounded border-secondary" : ""}
                        onClick={() => setSelectedImage(index + count)}
                    />
                </Figure>
            ))}
            <Button variant="light" onClick={() => setCount(prev => prev + 1)}
                    disabled={count === medias.length - renderCount || medias.length < renderCount}><i
                className="bi bi-chevron-right"/></Button>
        </Stack>

        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Body className="text-center">
                <Figure>
                    <Figure.Image
                        src={config.get_public_media(medias[selectedImage]?.path, "medium", darkMode)}
                    />
                </Figure>
            </Modal.Body>
        </Modal>
    </Stack>
}


function SummaryWithAlerts({value}) {
    return <>
        <AlertNote value={value}/>
        <div className="my-2"><Markdown>{value.summary}</Markdown></div>
    </>
}

function AlertNote({value}) {
    return <>
        {value.status === "archived" ?
            <Alert variant="danger" className="p-1"><i className="bi bi-exclamation-circle"/> Sorry, this product is no longer available for purchase or
                backorder.</Alert>
            : <>
                {value?.note && <Alert variant="success" className="p-2 mb-1"><i className="bi bi-info-circle"/> {value.note}</Alert>}
                {value?.alert && <Alert variant="warning" className="p-2"><i className="bi bi-exclamation-circle"/> {value.alert}</Alert>}
            </>
        }
    </>
}

function TitleField({value}) {
    const {admin} = useAuth()
    return <>
        <ShopPageTitle title={value.title}/>
        {admin ? <>
                <Link to={`/backend/product/${value.id}`}
                      className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                    <span>SKU: {value.SKU}</span>
                </Link>
                <NavTags value={value}/>
            </>
            :
            <span>SKU: {value.SKU}</span>
        }
    </>
}

function NavTags({value}) {
    return <div className="my-2 d-flex justify-content-start gap-2">
        {value.tags.map(item => (
            <Link key={item.id} to={`/shop/products?tags=${item.id}`}
                  className="link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover"
            > {item?.name}</Link>
        ))}
    </div>
}

function PriceSection({value}) {

    return <div>
        {value.status !== "archived" && <div>
            <QuantityToAdd prices={value.prices} prodId={value.id} stock={value.stock} backorder={value.is_available} status={value.status}
                           title={value.title}/>
        </div>}

        <div className="my-2">
            {value.stock > 0 && <div>
                <Badge bg="success" pill>IN STOCK</Badge> <span
                className="ms-1 text-muted">{value.stock > 10 ? "10+ available" : `${value.stock} available`}</span>
            </div>}
            {value.stock === 0 && value.is_available && <div>
                <Badge bg="warning" pill text="dark">BACKORDER</Badge> <span className="ms-1 text-muted">Estimated: 2-3 weeks</span>
            </div>}
            {value.stock === 0 && !value.is_available && <Badge bg="secondary" pill>OUT OF STOCK</Badge>}
        </div>

        {value.status !== "archived" && <PriceOptions prices={value.prices}/>}
    </div>
}

function QuantityToAdd({prices, prodId, stock, backorder, status, title}) {
    const {user} = useAuth()
    const [quantity, setQuantity] = useState(1);

    const {addArticle, errorArticle, loadingArticle, clearErrorArticle} = useBasket(false)
    const {success} = useAlert()

    const increment = () => {
        setQuantity(prevQuantity => prevQuantity + 1);
    }
    const decrement = () => {
        setQuantity(prevQuantity => (prevQuantity > 1 ? prevQuantity - 1 : 1));
    };

    function handleSubmit() {
        addArticle(prodId, quantity)
            .then(msg => {
                success(<span><strong>{title}</strong> has been added to your <Alert.Link as={Link} to="/shop/cart">Cart</Alert.Link>!</span>)
            })
            .catch(console.warn)
    }

    function getPriceForQuantity(quantity) {
        return prices
            .filter(item => item && item.quantity != null)
            .sort((a, b) => a.quantity - b.quantity)
            .reduce((acc, item) => quantity >= item.quantity ? item.price : acc, null)
    }

    return <div>
        <UserPrice basePrice={prices[0].price} unitPrice={getPriceForQuantity(quantity)} vat={user?.vat_percentage} className="fs-2 fw-bold"/>

        <div className="d-flex flex-wrap justify-content-center justify-content-md-start  gap-2 mt-2">
            <OurClearAlert variant="danger" msg={errorArticle} onClear={clearErrorArticle}/>

            {status !== "archived" && <div className="">
                <AdjustQuantity value={quantity} onChange={e => setQuantity(e.target.value)} onDecrement={decrement} onIncrement={increment} ch={10}/>
            </div>}
            <div className="flex-grow-1 ">
                {stock > 0 &&
                    <Button variant="success" className="w-100" onClick={handleSubmit}
                            disabled={loadingArticle}>{loadingArticle ? "Adding..." : "Add to Cart"}</Button>}
                {stock === 0 && backorder &&
                    <Button variant="warning" className="w-100" onClick={handleSubmit}
                            disabled={loadingArticle}>{loadingArticle ? "Adding..." : "Backorder"}</Button>}
                {stock === 0 && !backorder &&
                    <Button variant="success" className="w-100" onClick={handleSubmit} disabled>Add to Cart</Button>}
            </div>
        </div>
    </div>
}

function PriceOptions({prices}) {
    const [open, setOpen] = useState(false);
    const isMobile = window.innerWidth < 768

    if (prices.length <= 1) return null

    return <div className="mt-3">
        {
            isMobile ?
                <>
                    <Button
                        onClick={() => setOpen(!open)}
                        aria-controls="collapse-price-options"
                        aria-expanded={open}
                        variant="link"
                        className="ps-0"
                    >
                        Price Options
                    </Button>
                    <Collapse in={open}>
                        <div id="collapse-price-options">
                            <PriceTable prices={prices}/>
                        </div>
                    </Collapse>
                </>
                : <PriceTable prices={prices}/>
        }
    </div>
}

function PriceTable({prices}) {
    const {user} = useAuth()

    return <>
        <Table className="">
            <thead>
            <tr>
                <th>Qty.</th>
                <th>Price</th>
            </tr>
            </thead>
            <tbody>
            {prices.map((item, index) => (
                <tr key={index}>
                    <td>{item.quantity}</td>
                    <td>€ {centsToString(getPriceWithVat(item.price, user?.vat_percentage))}</td>
                </tr>
            ))}
            </tbody>
        </Table>
        <small className="text-muted">{user?.vat_percentage > 0 ? `Price includes ${user?.vat_percentage}% VAT` : "VAT not included in price"}</small>
    </>
}

function RelatedProducts({related}) {
    const [count, setCount] = useState(0);
    const {width} = useWindowSize()
    const renderCount = width < 576 ? 2 : 3

    const filteredRelated = related.filter((item, index) => index < renderCount + count && index >= count)

    return <>
        <div className="fs-4 fw-semibold text-center my-2">Related Products</div>

        <Stack direction="horizontal" className="justify-content-center align-items-center" gap={1}>
            <Button variant="light" onClick={() => setCount(prev => prev - 1)} disabled={count === 0}><i className="bi bi-chevron-left"/></Button>
            {filteredRelated.map((item, index) => (
                <div
                    key={index}
                    className="card border-0 bg-light-subtle"
                    style={{width: "220px", height: "250px", overflow: 'hidden'}}
                >
                    <RenderRelatedProducts key={index} pid={item.id}/>
                </div>
            ))}
            <Button variant="light" onClick={() => setCount(prev => prev + 1)}
                    disabled={count === related.length - renderCount || related.length < renderCount}><i className="bi bi-chevron-right"/></Button>
        </Stack>
    </>
}

function RenderRelatedProducts({pid}) {
    const {value: product, loadingValue, errorValue, clearError} = useProductDetails(pid)

    return (
        product && !loadingValue ? <>
                <Alert variant="danger" show={errorValue} dismissible onClose={clearError}>{errorValue}</Alert>
                <div className="d-flex flex-column align-items-center p-1">

                    <Figure className="d-flex justify-content-center">
                        <Link to={`/shop/detail/${product.id}`}>
                            <Figure.Image
                                src={config.get_public_media(product.medias[0]?.path, "thumb")}
                                alt={product.title}
                                rounded
                                height="120px"
                                width="120px"
                                loading="lazy"
                            />
                        </Link>
                    </Figure>

                    <Link to={`/shop/detail/${product.id}`}
                          className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                        <span className="text-wrap fw-medium">{product.title}</span>
                    </Link>

                    <div className="fw-medium">€ {centsToString(product.prices[0].price)}</div>

                </div>
            </>
            : <ProductCardLoading show={loadingValue}/>
    )
}
